import {
  CheckCircleFilled,
  EditOutlined,
  ExclamationCircleFilled,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { IconButton } from '@mui/material';
import { Alert, Button, Collapse, Modal, Select, Space, Spin, Typography, message } from 'antd';
import MultiSelect from 'components/MultiSelectorSearch';
import * as Sentry from '@sentry/react';
import { cloneDeep, filter, find, get, has, map, orderBy, sortBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  handleListAllBooks,
  handleGetBookByChapter,
  handleUpdateBookGrade,
  handleActivityGradeConfig,
  handleCheckActivityCount,
} from 'store/books/thunks';
import { useAllBook } from 'store/books/selectors';
import { useStandards } from 'store/story/selectors';
import { handleAutoSelectStandardsForAllQuestions } from 'store/story/thunks';
import { apis } from 'utils/lib/apis';
import { QUESTION_TYPE_ACTIVITY_NON_NATIVE, STANDARD_GRADE_LEVEL } from 'utils/config/reference';
import {
  compareGradesForIllinoisCommonCore,
  getStateNameDisplayForCode,
  getStateNameFromCode,
  statesMatch,
  normalizeGradeLevel,
  isIllinoisOrCommonCoreState,
} from 'utils/lib/standardHelper';
import { getNumberMask } from 'utils/lib/ebookHelper';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  align-items: flex-end;
`;

const QuestionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const ExamTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
  background: ${(p) => (p.$variant === 'unit' ? '#0369a1' : '#0f766e')};
  color: #fff;
`;

const QuestionTypeCapsule = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.3;
  flex-shrink: 0;
  max-width: 220px;
  background: #f5f5f5;
  color: #434343;
  border: 1px solid #d9d9d9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuestionCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
  flex-shrink: 0;
  background: #f9f0ff;
  color: #531dab;
  border: 1px solid #d3adf7;
  white-space: nowrap;
`;

const getQuestionTypeLabel = (type) => {
  if (!type) return '';
  const found = QUESTION_TYPE_ACTIVITY_NON_NATIVE.find((t) => t.value === type);
  return found?.type || type;
};

const getAssignmentExamFlags = (record, chapterId, books) => {
  const isUnitExam = Boolean(get(record, 'is_unit_exam') || get(record, 'data.is_unit_exam'));
  const book = find(books, (b) => String(b?.id) === String(chapterId));
  const isExamAssignment =
    Boolean(get(record, 'is_exam') || get(record, 'data.is_exam')) || Boolean(book?.quizAssessment);
  return { isUnitExam, isExamAssignment };
};

const getGradeLevelsForState = (state) => {
  if (isIllinoisOrCommonCoreState(state)) {
    return ['6', '7', '8', '9-10', '11-12'];
  }
  return ['6', '7', '8', '9', '10', '11', '12'];
};

const normalizeGradeForDropdown = (gradeLevel, state) => {
  if (!gradeLevel) return gradeLevel;
  const normalized = normalizeGradeLevel(gradeLevel);
  if (isIllinoisOrCommonCoreState(state)) {
    if (normalized === '9' || normalized === '10') {
      return '9-10';
    }
    if (normalized === '11' || normalized === '12') {
      return '11-12';
    }
  }
  return normalized;
};

const stripHtml = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const sortBooksById = (books) => {
  if (!Array.isArray(books)) return [];
  return sortBy(books, (b) => parseInt(String(b.id), 10) || 0);
};

const passesActivityFilters = (row) => {
  if (String(row.type || '').toLowerCase() === 'custom') return false;
  if (String(row.activity_type || '').toLowerCase() === 'graphic_organizer') return false;
  return true;
};

const gradeTokens = (gradeStr) =>
  String(gradeStr || '')
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean);

const standardCodesFromEntry = (entry) => {
  if (!entry || typeof entry === 'string') return [];
  const codes = get(entry, 'standard', []);
  const arr = Array.isArray(codes) ? codes : codes ? [codes] : [];
  return arr.map((c) => String(c || '').trim()).filter(Boolean);
};

const questionHasAnyStandards = (question) => {
  const list = get(question, 'state_standard', []);
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.some((entry) => standardCodesFromEntry(entry).length > 0);
};

const activityAnyStandardsStatus = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) return 'none';
  const total = questions.length;
  const tagged = questions.filter(questionHasAnyStandards).length;
  if (tagged === total) return 'complete';
  if (tagged > 0 && tagged < total) return 'partial';
  return 'none';
};

const questionMatchesFilter = (question, stateCode, gradeStr) => {
  if (!stateCode || !gradeStr) return false;
  const displayState = getStateNameFromCode(stateCode) || stateCode;
  const list = get(question, 'state_standard', []);
  if (!Array.isArray(list) || list.length === 0) return false;
  const hit = list.find((entry) => {
    const name = typeof entry === 'string' ? entry : get(entry, 'standard_name', '');
    const g = typeof entry === 'string' ? '' : get(entry, 'grade', '');
    const arr = standardCodesFromEntry(entry);
    if (!statesMatch(name, displayState)) return false;
    const ebookGrades = gradeTokens(gradeStr);
    if (ebookGrades.length === 0) return arr.length > 0;
    if (!g) return arr.length > 0;
    return ebookGrades.some((eg) => compareGradesForIllinoisCommonCore(eg, g, displayState)) && arr.length > 0;
  });
  return Boolean(hit);
};

const countTaggedQuestions = (questions, stateCode, gradeStr) => {
  if (!Array.isArray(questions) || questions.length === 0) return 0;
  return questions.filter((q) => questionMatchesFilter(q, stateCode, gradeStr)).length;
};

const activityStandardsStatus = (questions, stateCode, gradeStr) => {
  if (!stateCode || !gradeStr || !Array.isArray(questions) || questions.length === 0) {
    return 'none';
  }
  const total = questions.length;
  const tagged = countTaggedQuestions(questions, stateCode, gradeStr);
  if (tagged === total) return 'complete';
  if (tagged > 0 && tagged < total) return 'partial';
  return 'none';
};

const buildStandardOptions = (standardsList, stateDisplay, gradeLevel, ebookGrades) => {
  if (!Array.isArray(standardsList)) return [];
  return filter(standardsList, (k) => {
    if (get(k, 'status', false) !== true) return false;
    const standardState = get(k, 'state', '');
    const standardGradeLevel = get(k, 'grade_level', '');
    if (!statesMatch(standardState, stateDisplay)) return false;
    if (!gradeLevel) return true;
    if (ebookGrades.length === 0) return true;
    if (!standardGradeLevel) return true;
    const standardGrades = String(standardGradeLevel)
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
    return ebookGrades.some((ebookGrade) =>
      standardGrades.some((standardGrade) =>
        compareGradesForIllinoisCommonCore(ebookGrade, standardGrade, standardState),
      ),
    );
  }).map((k) => k.abbr);
};

const stripKind = (record) => {
  if (!record) return record;
  const { _kind, ...rest } = record;
  return rest;
};

const mergeAiIntoQuestions = (questions, results) => {
  const next = cloneDeep(questions || []);
  (results || []).forEach((item) => {
    const qi = item.questionIndex;
    if (next[qi] == null) return;
    const incomingBlocks = item.state_standard || [];
    const merged = Array.isArray(next[qi].state_standard) ? [...next[qi].state_standard] : [];
    incomingBlocks.forEach((block) => {
      const rawName = block.standard_name || '';
      const displayName = getStateNameFromCode(rawName) || rawName;
      const idx = merged.findIndex((s) => {
        const sState = typeof s === 'string' ? s : get(s, 'standard_name', '');
        return statesMatch(sState, displayName);
      });
      const nextBlock = {
        standard_name: displayName,
        grade: block.grade != null ? String(block.grade) : null,
        standard: Array.isArray(block.standard) ? block.standard : [],
      };
      if (idx >= 0) {
        merged[idx] = { ...merged[idx], ...nextBlock };
      } else {
        merged.push(nextBlock);
      }
    });
    next[qi].state_standard = merged;
  });
  return next;
};

const StandardsDashboardView = ({ userloading }) => {
  const dispatch = useDispatch();
  const booksAllList = useAllBook();
  const standards = useStandards();
  const [bookId, setBookId] = useState('');
  const [standardProfile, setStandardProfile] = useState('');
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [aiLoadingId, setAiLoadingId] = useState('');
  const [editCtx, setEditCtx] = useState(null);
  const [editQuestions, setEditQuestions] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [wholeBookModalOpen, setWholeBookModalOpen] = useState(false);
  const [bookToEditGrade, setBookToEditGrade] = useState(null);
  const [selectedStates, setSelectedStates] = useState('Common Core');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [gradeLoader, setGradeLoader] = useState(false);
  const [activityCount, setActivityCount] = useState(null);
  const [activityCountLoader, setActivityCountLoader] = useState(false);
  const [replaceGradeModal, setReplaceGradeModal] = useState(false);
  const [pendingGradeUpdate, setPendingGradeUpdate] = useState(null);

  useEffect(() => {
    if (!booksAllList?.length) {
      dispatch(handleListAllBooks());
    }
  }, [booksAllList?.length, dispatch]);

  const sortedBooks = useMemo(() => sortBooksById(booksAllList || []), [booksAllList]);

  const selectedBook = useMemo(
    () => find(sortedBooks, (b) => String(b.id) === String(bookId)),
    [sortedBooks, bookId],
  );

  const standardProfileOptions = useMemo(() => {
    const gl = selectedBook?.grade_levels || [];
    if (!Array.isArray(gl) || gl.length === 0) return [];
    return gl
      .map((item) => {
        const code = String(get(item, 'state', '') || '').trim();
        const grade = String(get(item, 'grade_level', '') || '').trim();
        if (!code || !grade) return null;
        const ref = find(
          STANDARD_GRADE_LEVEL,
          (s) =>
            String(s.code || '').toUpperCase() === code.toUpperCase() ||
            String(s.state || '').toUpperCase() === code.toUpperCase(),
        );
        if (!ref) return null;
        const label = `${ref.Description || ref.state} — Grade ${grade}`;
        return { value: `${code}|${grade}`, label, code, grade };
      })
      .filter(Boolean);
  }, [selectedBook]);

  useEffect(() => {
    if (!bookId) {
      setStandardProfile('');
      return;
    }
    if (!standardProfileOptions.length) {
      setStandardProfile('');
      return;
    }
    const exists = standardProfileOptions.some((o) => o.value === standardProfile);
    if (!exists) {
      setStandardProfile(standardProfileOptions[0].value);
    }
  }, [bookId, standardProfile, standardProfileOptions]);

  const parsedProfile = useMemo(() => {
    if (!standardProfile || !standardProfile.includes('|')) return { code: '', grade: '' };
    const sep = standardProfile.indexOf('|');
    const code = standardProfile.slice(0, sep).trim();
    const grade = standardProfile.slice(sep + 1).trim();
    return { code, grade };
  }, [standardProfile]);

  const showWholeBookStandardsBanner = Boolean(
    bookId && selectedBook && !fetching && standardProfileOptions.length === 0,
  );

  const gradeDropdownLevels = useMemo(() => getGradeLevelsForState(selectedStates), [selectedStates]);

  const checkActivityCount = useCallback(async () => {
    if (!bookToEditGrade || !selectedGrade || !selectedStates) {
      return;
    }
    setActivityCountLoader(true);
    try {
      let stateForAPI =
        selectedStates && selectedStates.toUpperCase().trim() === 'COMMON CORE'
          ? 'COMMONCORE'
          : getStateNameFromCode(selectedStates) || selectedStates;
      if (stateForAPI !== 'COMMONCORE') {
        stateForAPI = stateForAPI.toUpperCase().trim();
      }
      const countResult = await dispatch(
        handleCheckActivityCount({
          bookId: bookToEditGrade.id,
          grade_level: selectedGrade,
          state: stateForAPI,
        }),
      );
      if (countResult && countResult?.success && countResult?.status_code === 200) {
        setActivityCount(countResult?.data || null);
      } else {
        setActivityCount(null);
      }
    } catch {
      setActivityCount(null);
    } finally {
      setActivityCountLoader(false);
    }
  }, [bookToEditGrade, dispatch, selectedGrade, selectedStates]);

  const loadChapterContent = useCallback(async () => {
    if (!bookId) {
      setRows([]);
      return false;
    }
    setFetching(true);
    try {
      const [activitiesRaw, quizzesRaw] = await Promise.all([
        apis.get('activitiesRDS', `/activitiesByChapter/${bookId}`),
        apis.get('quizzesRDS', `/quizzesByChapter/${bookId}`),
      ]);
      const activities = Array.isArray(activitiesRaw) ? activitiesRaw : [];
      const quizzes = Array.isArray(quizzesRaw) ? quizzesRaw : [];
      const merged = [
        ...filter(activities, passesActivityFilters).map((a) => ({ ...a, _kind: 'activity' })),
        ...filter(quizzes, passesActivityFilters).map((q) => ({ ...q, _kind: 'quiz' })),
      ];
      setRows(orderBy(merged, [(r) => Number(r.page) || 0], ['asc']));
      return true;
    } catch (error) {
      message.error('Could not load activities for this book.');
      Sentry.captureException(error);
      setRows([]);
      return false;
    } finally {
      setFetching(false);
    }
  }, [bookId]);

  useEffect(() => {
    loadChapterContent();
  }, [loadChapterContent]);

  const handleRefresh = useCallback(async () => {
    if (!bookId) {
      message.warning('Select a book to refresh activities and quizzes');
      return;
    }
    const ok = await loadChapterContent();
    if (ok) {
      message.success('Activities and quizzes refreshed');
    }
  }, [bookId, loadChapterContent]);

  useEffect(() => {
    if (wholeBookModalOpen !== true || !bookToEditGrade?.id) {
      return undefined;
    }
    const fetchBookDetails = async () => {
      try {
        const bookResponse = await dispatch(handleGetBookByChapter(bookToEditGrade.id));
        if (bookResponse && bookResponse[0]) {
          setBookToEditGrade(bookResponse[0]);
        }
      } catch {
        // ignore
      }
    };
    fetchBookDetails();
  }, [wholeBookModalOpen, bookToEditGrade?.id, dispatch]);

  useEffect(() => {
    if (wholeBookModalOpen !== true || !bookToEditGrade) {
      return undefined;
    }
    setSelectedStates('Common Core');
    const gradeLevels = getGradeLevelsForState('Common Core');
    if (bookToEditGrade?.grade_level) {
      const normalizedGrade = normalizeGradeForDropdown(bookToEditGrade.grade_level, 'Common Core');
      if (gradeLevels.includes(normalizedGrade)) {
        setSelectedGrade(normalizedGrade);
      } else {
        setSelectedGrade(gradeLevels[0] || '');
      }
    } else {
      setSelectedGrade(gradeLevels[0] || '');
    }
    return undefined;
  }, [wholeBookModalOpen, bookToEditGrade]);

  useEffect(() => {
    if (!selectedStates || !wholeBookModalOpen) {
      return undefined;
    }
    const gradeLevels = getGradeLevelsForState(selectedStates);
    const currentGrade = selectedGrade;
    if (currentGrade) {
      const normalizedGrade = normalizeGradeForDropdown(currentGrade, selectedStates);
      if (gradeLevels.includes(normalizedGrade)) {
        setSelectedGrade(normalizedGrade);
      } else {
        setSelectedGrade(gradeLevels[0] || '');
      }
    } else {
      setSelectedGrade(gradeLevels[0] || '');
    }
    return undefined;
  }, [selectedStates]);

  useEffect(() => {
    if (selectedGrade && selectedStates) {
      checkActivityCount();
    }
  }, [selectedGrade, selectedStates, checkActivityCount]);

  const handleWholeBookGradeUpdate = async (replace = false) => {
    if (!selectedGrade || !bookToEditGrade) return;
    const totalStandards = activityCount?.allStandardAbbr?.length || 0;
    if (totalStandards === 0) {
      const stateName = getStateNameFromCode(selectedStates) || selectedStates;
      message.error(
        `For state ${stateName} there is no standard. If you want to run for this state, add the standard. Otherwise, change the state and run the standard.`,
      );
      return;
    }
    setGradeLoader(true);
    try {
      let stateForAPI =
        selectedStates && selectedStates.toUpperCase().trim() === 'COMMON CORE'
          ? 'COMMONCORE'
          : getStateNameFromCode(selectedStates) || selectedStates;
      if (stateForAPI !== 'COMMONCORE') {
        stateForAPI = stateForAPI.toUpperCase().trim();
      }
      const gradeData = {
        grade_level: selectedGrade,
        state: stateForAPI,
        replace,
      };
      const updateResult = await dispatch(handleUpdateBookGrade(bookToEditGrade.id, gradeData));
      if (updateResult && updateResult.error) {
        message.error('Failed to update book grade level');
        setGradeLoader(false);
        return;
      }
      await dispatch(
        handleActivityGradeConfig({
          bookId: bookToEditGrade.id,
          grade_level: selectedGrade,
          state: stateForAPI,
          replace,
        }),
      );
      await checkActivityCount();
      message.success(
        'Grade level updated successfully. Processing activities, it will take some time to complete.',
      );
      setWholeBookModalOpen(false);
      setBookToEditGrade(null);
      setSelectedGrade('');
      setSelectedStates('Common Core');
      setReplaceGradeModal(false);
      setPendingGradeUpdate(null);
      setActivityCount(null);
      await loadChapterContent();
    } catch {
      message.error('Failed to update grade level');
    } finally {
      setGradeLoader(false);
    }
  };

  const openWholeBookStandardsModal = () => {
    if (!bookId) {
      message.warning('Select a book first.');
      return;
    }
    const row = find(sortedBooks, (b) => String(b.id) === String(bookId));
    if (!row) {
      message.warning('Book not found in list.');
      return;
    }
    setActivityCount(null);
    setReplaceGradeModal(false);
    setPendingGradeUpdate(null);
    setBookToEditGrade(row);
    setWholeBookModalOpen(true);
  };

  const closeWholeBookStandardsModal = () => {
    setWholeBookModalOpen(false);
    setBookToEditGrade(null);
    setSelectedGrade('');
    setSelectedStates('Common Core');
    setActivityCount(null);
    setActivityCountLoader(false);
    setReplaceGradeModal(false);
    setPendingGradeUpdate(null);
  };

  const openEdit = (record, questionIndex) => {
    setEditCtx({ record, questionIndex });
    setEditQuestions(cloneDeep(record.questions || []));
  };

  const handleStateStandardChange = (selectedValues, questionIndex, stateIndex, standardObj) => {
    setEditQuestions((prev) => {
      const updatedQuestions = cloneDeep(prev);
      if (!updatedQuestions[questionIndex].state_standard) {
        updatedQuestions[questionIndex].state_standard = [];
      }
      if (!Array.isArray(updatedQuestions[questionIndex].state_standard)) {
        updatedQuestions[questionIndex].state_standard = [];
      }
      const targetStateName = standardObj?.standard_name || '';
      const existingIndex = updatedQuestions[questionIndex].state_standard.findIndex((s) => {
        const sState = typeof s === 'string' ? s : get(s, 'standard_name', '');
        return statesMatch(sState, targetStateName);
      });
      const nextStandard = Array.isArray(selectedValues)
        ? selectedValues
        : typeof selectedValues === 'string' && selectedValues
          ? selectedValues.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
      if (existingIndex >= 0) {
        updatedQuestions[questionIndex].state_standard[existingIndex] = {
          ...updatedQuestions[questionIndex].state_standard[existingIndex],
          standard_name: targetStateName,
          grade: standardObj?.grade || updatedQuestions[questionIndex].state_standard[existingIndex]?.grade || null,
          standard: nextStandard,
        };
      } else {
        updatedQuestions[questionIndex].state_standard.push({
          standard_name: targetStateName,
          grade: standardObj?.grade || null,
          standard: nextStandard,
        });
      }
      return updatedQuestions;
    });
  };

  const saveEdit = async () => {
    if (!editCtx?.record) return;
    setSavingEdit(true);
    try {
      const base = editCtx.record;
      const payload = stripKind({ ...base, questions: editQuestions });
      const res =
        base._kind === 'quiz'
          ? await apis.put('quizzesRDS', `/update/${base.id}`, { body: payload })
          : await apis.put('activitiesRDS', `/update/${base.id}`, { body: payload });
      if (!has(res, 'id')) {
        throw new Error('Update rejected');
      }
      message.success('Saved');
      setEditCtx(null);
      await loadChapterContent();
    } catch (error) {
      message.error('Save failed');
      Sentry.captureException(error);
    } finally {
      setSavingEdit(false);
    }
  };

  const runAiForRow = useCallback(
    async (record) => {
      if (!bookId || !record?.questions?.length) {
        message.warning('Nothing to process for this item.');
        return;
      }
      const gl = selectedBook?.grade_levels || [];
      if (!Array.isArray(gl) || gl.length === 0) {
        message.warning('This book has no grade levels configured.');
        return;
      }
      setAiLoadingId(`${record._kind}-${record.id}`);
      try {
        const questionsPayload = record.questions.map((q, index) => ({
          type: q.type,
          id: q.id || index + 1,
          expected_answer: q.expected_answer || [],
          question: q.question || '',
        }));
        const result = await dispatch(
          handleAutoSelectStandardsForAllQuestions({
            questions: questionsPayload,
            bookId: String(bookId),
            pageId: record.page,
            gradeLevels: gl,
          }),
        );
        if (!Array.isArray(result) || result.length === 0) {
          message.warning('No standards were returned. Try again or edit manually.');
          return;
        }
        const updatedQuestions = mergeAiIntoQuestions(record.questions, result);
        const payload = stripKind({ ...record, questions: updatedQuestions });
        const res =
          record._kind === 'quiz'
            ? await apis.put('quizzesRDS', `/update/${record.id}`, { body: payload })
            : await apis.put('activitiesRDS', `/update/${record.id}`, { body: payload });
        if (!has(res, 'id')) {
          throw new Error('Update rejected');
        }
        message.success('Standards updated with AI');
        await loadChapterContent();
      } catch (error) {
        message.error('AI standards failed');
        Sentry.captureException(error);
      } finally {
        setAiLoadingId('');
      }
    },
    [bookId, selectedBook, dispatch, loadChapterContent],
  );

  const renderLabel = (value) => getStateNameDisplayForCode(value) || value;

  const collapseItems = useMemo(() => {
    return map(rows, (record) => {
      const mask = getNumberMask(String(bookId), record.page, sortedBooks);
      const titleHtml = record.displayName || record.title || 'Untitled';
      const { isUnitExam, isExamAssignment } = getAssignmentExamFlags(record, bookId, sortedBooks);
      const questionCount = Array.isArray(record.questions) ? record.questions.length : 0;
      const hasStandardProfile = Boolean(parsedProfile.code && parsedProfile.grade);
      const standardsStatus =
        record._kind === 'activity' || record._kind === 'quiz'
          ? hasStandardProfile
            ? activityStandardsStatus(record.questions, parsedProfile.code, parsedProfile.grade)
            : activityAnyStandardsStatus(record.questions)
          : 'none';
      const header = (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              flex: 1,
              minWidth: 160,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: titleHtml }} />
            {(isUnitExam || isExamAssignment) && (
              <ExamTypeBadge $variant={isUnitExam ? 'unit' : 'exam'}>
                {isUnitExam ? 'UNIT EXAM' : 'EXAMEN'}
              </ExamTypeBadge>
            )}
          </span>
          <Typography.Text type="secondary">Page {mask || record.page}</Typography.Text>
          <QuestionCountBadge>
            {questionCount} {questionCount === 1 ? 'Question' : 'Questions'}
          </QuestionCountBadge>
          {standardsStatus === 'complete' ? (
            <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} aria-label="All questions tagged" />
          ) : null}
          {(standardsStatus === 'partial' || standardsStatus === 'none') ? (
            <ExclamationCircleFilled
              style={{ color: '#ff4d4f', fontSize: 18 }}
              aria-label={
                standardsStatus === 'partial'
                  ? 'Some questions missing standards'
                  : 'No questions tagged with standards'
              }
            />
          ) : null}
          <Button
            type="primary"
            size="small"
            icon={<ThunderboltOutlined />}
            loading={aiLoadingId === `${record._kind}-${record.id}`}
            onClick={(e) => {
              e.stopPropagation();
              runAiForRow(record);
            }}
          >
            Assign standards with AI
          </Button>
        </div>
      );
      const body = map(record.questions || [], (q, idx) => {
        const isAssignable = record._kind === 'activity' || record._kind === 'quiz';
        const ok = isAssignable
          ? hasStandardProfile
            ? questionMatchesFilter(q, parsedProfile.code, parsedProfile.grade)
            : questionHasAnyStandards(q)
          : false;
        const showQuestionStatus = isAssignable;
        const previewPlain = stripHtml(q.question || '').slice(0, 140) || `Question ${idx + 1}`;
        const typeLabel = getQuestionTypeLabel(q.type);
        return (
          <QuestionRow key={`q-${record.id}-${idx}`}>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Typography.Text ellipsis style={{ flex: 1, marginBottom: 0 }}>
                {previewPlain}
              </Typography.Text>
              {typeLabel ? (
                <QuestionTypeCapsule title={typeLabel}>{typeLabel}</QuestionTypeCapsule>
              ) : null}
            </span>
            {showQuestionStatus && ok ? (
              <CheckCircleFilled style={{ color: '#52c41a' }} aria-label="Standards tagged" />
            ) : null}
            {showQuestionStatus && !ok ? (
              <ExclamationCircleFilled style={{ color: '#ff4d4f' }} aria-label="Standards missing" />
            ) : null}
            <IconButton size="small" onClick={() => openEdit(record, idx)} aria-label="edit standards">
              <EditOutlined />
            </IconButton>
          </QuestionRow>
        );
      });
      return {
        key: `${record._kind}-${record.id}`,
        label: header,
        children: <div>{body}</div>,
      };
    });
  }, [rows, bookId, sortedBooks, parsedProfile, aiLoadingId, runAiForRow]);

  const editingQuestion = editCtx != null ? editQuestions[editCtx.questionIndex] : null;

  return (
    <div>
      <PageHeader>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Standards dashboard
        </Typography.Title>
        <Button
          type="text"
          onClick={handleRefresh}
          disabled={!bookId || fetching || Boolean(userloading)}
          aria-label="Refresh activities and quizzes"
          title="Refresh activities and quizzes"
        >Refresh<ReloadOutlined spin={fetching} /></Button>
      </PageHeader>
      <Toolbar>
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary">Book</Typography.Text>
          <Select
            showSearch
            allowClear
            placeholder="Select book"
            style={{ minWidth: 280 }}
            optionFilterProp="label"
            value={bookId || undefined}
            onChange={(v) => setBookId(v || '')}
            options={sortedBooks.map((b) => ({
              value: String(b.id),
              label: `${b.description || b.title || 'Book'} (${b.id})`,
            }))}
          />
        </Space>
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary">Standard profile</Typography.Text>
          <Select
            disabled={!standardProfileOptions.length}
            placeholder={selectedBook ? 'Select standard profile' : 'Select a book first'}
            style={{ minWidth: 320 }}
            value={standardProfile || undefined}
            onChange={setStandardProfile}
            options={standardProfileOptions.map((o) => ({ value: o.value, label: o.label }))}
          />
        </Space>
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary">Whole book</Typography.Text>
          <Button type="default" disabled={!bookId} onClick={openWholeBookStandardsModal}>
            Run standards (AI)
          </Button>
        </Space>
      </Toolbar>
      {showWholeBookStandardsBanner ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Standards have not been run for the selected book."
          description="Please apply standards to the whole book."
          action={
            <Button size="small" type="primary" onClick={openWholeBookStandardsModal}>
              Run standards (AI)
            </Button>
          }
        />
      ) : null}
      <Spin spinning={Boolean(userloading) || fetching}>
        {!bookId ? (
          <Typography.Text type="secondary">Choose a book to see activities and quizzes.</Typography.Text>
        ) : (
          <Collapse items={collapseItems} />
        )}
      </Spin>
      <Modal
        title={`Update grade level — ${get(bookToEditGrade, 'description', '')}`}
        open={wholeBookModalOpen}
        onCancel={closeWholeBookStandardsModal}
        footer={null}
        width={480}
        destroyOnClose
      >
        {bookToEditGrade && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ width: '100%' }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                States
              </Typography.Text>
              <Select
                style={{ width: '100%' }}
                value={selectedStates}
                onChange={(v) => setSelectedStates(v)}
                options={STANDARD_GRADE_LEVEL.map((stateConfig) => {
                  const stateName = getStateNameFromCode(stateConfig.code) || stateConfig.state;
                  const displayValue = getStateNameDisplayForCode(stateName) || stateName;
                  return { value: stateName, label: displayValue };
                })}
              />
            </div>
            <div style={{ width: '100%' }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Grade level
              </Typography.Text>
              <Select
                style={{ width: '100%' }}
                value={selectedGrade || undefined}
                onChange={(v) => setSelectedGrade(v)}
                options={gradeDropdownLevels.map((g) => ({ value: g, label: g }))}
              />
            </div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              Total number of standards:{' '}
              {activityCountLoader ? <Spin size="small" /> : activityCount?.allStandardAbbr?.length ?? 0}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              Activities remaining:{' '}
              {activityCountLoader ? (
                <Spin size="small" />
              ) : activityCount?.count !== null && activityCount?.count !== undefined ? (
                activityCount.count
              ) : (
                '-'
              )}
            </div>
            <Space style={{ marginTop: 8 }}>
              <Button onClick={closeWholeBookStandardsModal}>Cancel</Button>
              <Button
                type="primary"
                loading={gradeLoader}
                disabled={
                  !selectedGrade ||
                  gradeLoader ||
                  activityCountLoader ||
                  (activityCount?.count !== null &&
                    activityCount?.count !== undefined &&
                    activityCount.count === 0)
                }
                onClick={async () => {
                  if (!selectedGrade || !bookToEditGrade) return;
                  const totalStandards = activityCount?.allStandardAbbr?.length || 0;
                  if (totalStandards === 0) {
                    const stateName = getStateNameFromCode(selectedStates) || selectedStates;
                    message.error(
                      `For state ${stateName} there is no standard. If you want to run for this state, add the standard. Otherwise, change the state and run the standard.`,
                    );
                    return;
                  }
                  const bookGradeLevels = bookToEditGrade?.grade_levels || [];
                  const existingStateGrade = bookGradeLevels.find((gl) => statesMatch(gl.state, selectedStates));
                  if (existingStateGrade) {
                    const existingGradeLevel = existingStateGrade.grade_level;
                    const normalizedExisting = normalizeGradeForDropdown(existingGradeLevel, selectedStates);
                    const normalizedSelected = normalizeGradeForDropdown(selectedGrade, selectedStates);
                    if (normalizedExisting !== normalizedSelected) {
                      setPendingGradeUpdate({
                        existingGrade: normalizedExisting,
                        newGrade: normalizedSelected,
                        state: selectedStates,
                      });
                      setReplaceGradeModal(true);
                      return;
                    }
                  }
                  await handleWholeBookGradeUpdate(false);
                }}
              >
                Update
              </Button>
            </Space>
          </Space>
        )}
      </Modal>
      <Modal
        title="Replace grade level?"
        open={replaceGradeModal}
        onCancel={() => {
          setReplaceGradeModal(false);
          setPendingGradeUpdate(null);
        }}
        footer={[
          <Button
            key="no"
            onClick={() => {
              setReplaceGradeModal(false);
              setPendingGradeUpdate(null);
            }}
          >
            No
          </Button>,
          <Button
            key="yes"
            type="primary"
            loading={gradeLoader}
            onClick={async () => {
              await handleWholeBookGradeUpdate(true);
            }}
          >
            Yes
          </Button>,
        ]}
        destroyOnClose
      >
        <Typography.Paragraph>
          You have already run standards for grade level <strong>{pendingGradeUpdate?.existingGrade}</strong> for
          state <strong>{pendingGradeUpdate?.state}</strong>.
        </Typography.Paragraph>
        <Typography.Paragraph>
          If you proceed with grade level <strong>{pendingGradeUpdate?.newGrade}</strong>, the existing grade level{' '}
          <strong>{pendingGradeUpdate?.existingGrade}</strong> will be removed and replaced with the new one.
        </Typography.Paragraph>
      </Modal>
      <Modal
        title="Edit standards for question"
        open={Boolean(editCtx)}
        onCancel={() => setEditCtx(null)}
        onOk={saveEdit}
        confirmLoading={savingEdit}
        width={720}
        destroyOnClose
      >
        {editingQuestion && selectedBook && (
          <div>
            {map(selectedBook.grade_levels || [], (gradeLevelItem, stateIndex) => {
              const stateCode = get(gradeLevelItem, 'state', '');
              const state = getStateNameFromCode(stateCode) || stateCode;
              const gradeLevel = get(gradeLevelItem, 'grade_level', '');
              if (!state) return null;
              const standardObj = {
                standard_name: state,
                grade: gradeLevel,
              };
              const existingStateStandard =
                editingQuestion.state_standard && Array.isArray(editingQuestion.state_standard)
                  ? editingQuestion.state_standard.find((s) => {
                      const sState = typeof s === 'string' ? s : get(s, 'standard_name', '');
                      return statesMatch(sState, state);
                    })
                  : null;
              const standardArray = existingStateStandard
                ? Array.isArray(existingStateStandard.standard)
                  ? existingStateStandard.standard
                  : existingStateStandard.standard
                    ? [existingStateStandard.standard]
                    : []
                : [];
              const ebookGrades = String(gradeLevel)
                .split(',')
                .map((g) => g.trim())
                .filter((g) => g);
              const filterOptions = buildStandardOptions(standards, state, gradeLevel, ebookGrades);
              if (!filterOptions.length) return null;
              return (
                <div key={stateIndex} style={{ marginBottom: 16 }}>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {renderLabel(stateCode)}
                  </Typography.Text>
                  <MultiSelect
                    label=""
                    InputLabelProps={{ shrink: true }}
                    options={filterOptions}
                    selectedValue={standardArray}
                    onSelect={(v) => {
                      const selectedArray =
                        typeof v === 'string'
                          ? v
                            ? v.split(',').map((s) => s.trim()).filter(Boolean)
                            : []
                          : Array.isArray(v)
                            ? v
                            : [];
                      handleStateStandardChange(selectedArray, editCtx.questionIndex, stateIndex, standardObj);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StandardsDashboardView;
